import React from "react"
import { Button } from "@/components/ui/button"
import { RefreshCw, ArrowLeft, ChevronRight, Home } from "lucide-react"
import { Select } from "antd"

interface BreadcrumbItem {
  title: string
  onClick?: () => void
}

interface StatCard {
  label: string
  value: string | number
  icon?: React.ReactNode
}

interface ProductionPeriodConfig {
  value?: string
  options?: { label: string; value: string }[]
  onChange?: (value: string) => void
}

interface PageHeaderProps {
  title: string
  subtitle?: string
  onBack?: () => void
  onTitleClick?: () => void
  titleIcon?: React.ReactNode
  breadcrumbItems?: BreadcrumbItem[]
  showRefresh?: boolean
  onRefresh?: () => void
  extra?: React.ReactNode
  children?: React.ReactNode
  // Modo heróico
  heroic?: boolean
  heroLabel?: string
  stats?: StatCard[]
  productionPeriod?: ProductionPeriodConfig
  headerExtra?: React.ReactNode
}

function SimpleHeader({
  title,
  subtitle,
  onBack,
  breadcrumbItems,
  showRefresh = false,
  onRefresh,
  extra,
  children
}: Omit<PageHeaderProps, 'heroic' | 'heroLabel' | 'stats' | 'productionPeriod' | 'headerExtra'>) {
  return (
    <header className="h-auto min-h-[4rem] flex flex-col justify-center px-6 py-3 border-b border-[rgba(255,55,55,0.1)] bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md sticky top-0 z-10 shadow-[0_1px_4px_rgba(255,55,55,0.05)]">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          {/* Breadcrumbs */}
          {breadcrumbItems && (
            <nav className="flex items-center gap-1.5 mb-1.5 opacity-60">
              <Home className="w-3 h-3 text-[#94a3b8]" />
              {breadcrumbItems.map((item, index) => (
                <div key={index} className="flex items-center gap-1.5 text-[0.65rem] font-bold uppercase tracking-[0.1em]">
                  <ChevronRight className="w-3 h-3 text-[#cbd5e1]" />
                  {item.onClick ? (
                    <button
                      key={`bc-${index}`}
                      onClick={item.onClick}
                      className="text-[#94a3b8] hover:text-[#FF3737] transition-colors"
                    >
                      {item.title}
                    </button>
                  ) : (
                    <span key={`bc-text-${index}`} className="text-[#FF3737]">{item.title}</span>
                  )}
                </div>
              ))}
            </nav>
          )}

          <div className="flex items-center gap-3">
            {onBack && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                className="h-8 w-8 rounded-full bg-[#FF3737]/10 hover:bg-[#FF3737]/20 text-[#FF3737] dark:text-[#FF3737]"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div className="flex flex-col">
              {subtitle && !breadcrumbItems && (
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[0.6rem] font-bold text-[#94a3b8] uppercase tracking-[0.1em]">Portal</span>
                  <div className="h-1 w-1 rounded-full bg-[#cbd5e1]" />
                  <span className="text-[0.6rem] font-bold text-[#FF3737] uppercase tracking-[0.1em]">{subtitle}</span>
                </div>
              )}
              <h1 className="text-lg md:text-xl font-bold text-[#0f172a] dark:text-white leading-none tracking-tight">
                {title}
              </h1>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {showRefresh && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              className="gap-2 rounded-[12px] font-medium text-[#475569] dark:text-zinc-400 hover:bg-[rgba(255,55,55,0.08)] dark:hover:bg-white/10 hover:text-[#FF3737] dark:hover:text-[#FF3737] transition-all h-9"
            >
              <RefreshCw className="h-4 w-4 text-[#FF3737] dark:text-[#FF3737]" />
              <span className="hidden sm:inline text-xs">Atualizar</span>
            </Button>
          )}
          {extra}
          {children}
        </div>
      </div>
    </header>
  )
}

function HeroicHeader({
  title,
  heroLabel,
  stats,
  productionPeriod,
  headerExtra,
  onTitleClick,
  titleIcon,
}: {
  title: string
  heroLabel?: string
  stats?: StatCard[]
  productionPeriod?: ProductionPeriodConfig
  headerExtra?: React.ReactNode
  onTitleClick?: () => void
  titleIcon?: React.ReactNode
}) {
  return (
    <header className="bg-gradient-to-r from-[#0d0d0f] to-[#1c1c1f] text-white px-8 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 flex-shrink-0 shadow-sm relative overflow-hidden border-b border-white/10">
      {/* Decorative background gradients */}
      <div className="absolute right-0 top-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
      <div className="absolute left-0 bottom-0 w-48 h-48 bg-[#FF3737]/10 rounded-full blur-2xl -ml-20 -mb-20 pointer-events-none" />

      <div className="flex flex-col relative z-10 min-w-0">
        {heroLabel && (
          <p className="text-[10px] text-white/50 uppercase tracking-widest font-bold mb-2">
            {heroLabel}
          </p>
        )}
        {onTitleClick ? (
          <button
            type="button"
            onClick={onTitleClick}
            data-page-header-title-trigger=""
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity text-left"
          >
            <h1 className="text-xl md:text-3xl font-bold leading-tight whitespace-nowrap">
              {title}
            </h1>
            {titleIcon}
          </button>
        ) : (
          <h1 className="text-xl md:text-3xl font-bold leading-tight whitespace-nowrap">
            {title}
          </h1>
        )}
      </div>

      {headerExtra && (
        <div className="relative z-10">
          {headerExtra}
        </div>
      )}

      <div className="flex items-center gap-3 relative z-10 flex-shrink-0 flex-wrap justify-center md:justify-end">
        {productionPeriod && productionPeriod.options && productionPeriod.options.length > 0 && (
          <div className="flex flex-col items-start gap-1">
            <span className="text-xs text-white/60 uppercase tracking-wider font-semibold">Período de Produção</span>
            <Select
              value={productionPeriod.value}
              onChange={productionPeriod.onChange}
              options={productionPeriod.options}
              className="w-56 [&_.ant-select-selector]:bg-white/15 [&_.ant-select-selector]:border-white/30 [&_.ant-select-selector]:text-white [&_.ant-select-selection-item]:text-white"
              popupMatchSelectWidth={false}
            />
          </div>
        )}
      </div>

      {/* Stats Section */}
      {stats && stats.length > 0 && (
        <div className="w-full relative z-10 mt-6 md:mt-0">
          <div className="flex flex-wrap gap-4 justify-center md:justify-end">
            {stats.map((stat, idx) => (
              <div key={idx} className="flex items-center gap-2">
                {stat.icon && <span className="text-lg">{stat.icon}</span>}
                <div className="text-sm">
                  <p className="text-white/70 text-xs uppercase tracking-wider">{stat.label}</p>
                  <p className="text-white font-bold">{stat.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  )
}

export function PageHeader(props: PageHeaderProps) {
  const { heroic = false } = props

  if (heroic) {
    return (
      <HeroicHeader
        title={props.title}
        heroLabel={props.heroLabel}
        stats={props.stats}
        productionPeriod={props.productionPeriod}
        headerExtra={props.headerExtra}
        onTitleClick={props.onTitleClick}
        titleIcon={props.titleIcon}
      />
    )
  }

  return (
    <SimpleHeader
      title={props.title}
      subtitle={props.subtitle}
      onBack={props.onBack}
      breadcrumbItems={props.breadcrumbItems}
      showRefresh={props.showRefresh}
      onRefresh={props.onRefresh}
      extra={props.extra}
      children={props.children}
    />
  )
}
